import {defineSlotRecipe} from '@chakra-ui/react3'

export default defineSlotRecipe({
    slots: [
        'root',
        'container',
        'slider',
        'slide',
        'slideText',
        'copy',
        'cta',
        'prevControl',
        'controls',
        'controlButton'
    ],
    base: {
        root: {
            width: '100%',
            height: 'spacing-44',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
        },
        container: {
            position: 'relative',
            width: '100%',
            minWidth: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        slider: {
            position: 'relative',
            width: '100%',
            height: '100%',
            minWidth: 0,
            display: 'grid',
            // `minmax(0, 1fr)` keeps long copy from widening the column
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateAreas: '"slide"'
        },
        // All slides share one grid cell. A switch runs in two halves of
        // `--message-bar-speed`: the outgoing slide fades out upwards, then the
        // incoming slide fades in from below.
        slide: {
            '--message-bar-speed': '500ms',
            '--message-bar-half-speed': 'calc(var(--message-bar-speed) / 2)',
            gridArea: 'slide',
            width: '100%',
            minWidth: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            paddingInline: {base: 'spacing-68', s: 'spacing-48'},
            opacity: 0,
            transform: 'translateY(8px)',
            // `visibility` keeps hidden slides out of the tab order and the a11y
            // tree; being transitioned, it holds the outgoing slide visible for
            // its whole fade-out.
            visibility: 'hidden',
            transitionProperty: 'opacity, transform, visibility',
            transitionDuration: 'var(--message-bar-half-speed)',
            transitionTimingFunction: 'ease-in-out',
            '&[data-state="leaving"]': {
                transform: 'translateY(-8px)'
            },
            '&[data-state="active"]': {
                opacity: 1,
                transform: 'translateY(0)',
                visibility: 'visible',
                transitionDelay: 'var(--message-bar-half-speed)'
            },
            _motionReduce: {
                transitionDuration: '0.01ms',
                transitionDelay: '0s !important'
            }
        },
        slideText: {
            textStyle: 'body.small',
            display: 'inline-flex',
            flexDirection: {base: 'column', s: 'row'},
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: 'spacing-8',
            maxWidth: '100%'
        },
        // Long copy stays on one line and ends with an ellipsis
        copy: {
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
        },
        cta: {
            flexShrink: 0,
            textStyle: 'body.small',
            color: 'inherit',
            _hover: {
                opacity: 0.85
            }
        },
        prevControl: {
            position: 'absolute',
            insetInlineStart: {base: 'spacing-8', s: 'spacing-16'},
            display: 'inline-flex',
            alignItems: 'center',
            zIndex: 1
        },
        controls: {
            position: 'absolute',
            insetInlineEnd: {base: 'spacing-8', s: 'spacing-16'},
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'spacing-4',
            zIndex: 1
        },
        controlButton: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '1.5rem',
            height: '1.5rem',
            padding: '0',
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            opacity: 0.8,
            borderRadius: 'xs',
            _hover: {
                opacity: 1
            },
            _focusVisible: {
                outline: '2px solid currentColor',
                outlineOffset: '2px'
            },
            // sprite svg has no size, so Safari collapses it to 0×0
            _icon: {
                width: '1.5rem',
                height: '1.5rem'
            }
        }
    },
    variants: {
        // On mobile the CTA sits below the copy and needs the taller bar
        hasCta: {
            true: {
                root: {
                    height: {
                        base: 'spacing-64',
                        s: 'spacing-44'
                    }
                }
            },
            false: {}
        },
        color: {
            neutral: {
                root: {
                    backgroundColor: 'secondary.cream-100',
                    color: 'text.on-color-inverted'
                }
            },
            cream: {
                root: {
                    backgroundColor: 'secondary.cream',
                    color: 'text.on-media.primary'
                }
            },
            fjordBlue: {
                root: {
                    backgroundColor: 'secondary.fjordBlue',
                    color: 'text.on-surface'
                }
            },
            cherry: {
                root: {
                    backgroundColor: 'secondary.cherry',
                    color: 'text.on-surface'
                }
            },
            pineGreen: {
                root: {
                    backgroundColor: 'secondary.pineGreen',
                    color: 'text.on-media.primary'
                }
            },
            terracotta: {
                root: {
                    backgroundColor: 'secondary.terracotta',
                    color: 'text.on-media.primary'
                }
            },
            malva: {
                root: {
                    backgroundColor: 'secondary.malva',
                    color: 'text.on-surface'
                }
            },
            sunflower: {
                root: {
                    backgroundColor: 'secondary.sunflower',
                    color: 'text.on-color-inverted'
                }
            },
            black: {
                root: {
                    backgroundColor: 'primary.base.black',
                    color: 'text.on-surface'
                }
            }
        }
    },
    defaultVariants: {
        hasCta: false,
        color: 'neutral'
    }
})
